// ═══════════════════════════════════════════════════════════════════════════════
// Receipt AI Analysis — helper: pick images, analyze, edit, submit
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReceiptImageLightbox } from '../../components/receipt/ReceiptImageLightbox';
import {
  DEFAULT_CURRENCY,
  formatReceiptMismatchDateLabel,
  formatReceiptMoney,
  getTodayDate,
  isReceiptTotalMismatch,
  parseReceiptTotalAmount,
  RECEIPT_STATUS,
  sumReceiptLineItemAmounts,
  type AnalyzeResult,
  type ReceiptData,
  type ReceiptItem,
  type ReceiptStatus,
} from '@fdh/shared';
import { HelperSettlementNextStepSheet } from '../../components/settlement/HelperSettlementNextStepSheet';
import { ReceiptTotalMismatchBanner } from '../../components/receipt/ReceiptTotalMismatchBanner';
import { StatusBadge } from '../../components/task/StatusBadge';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { AppButton } from '../../components/ui/AppButton';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { PagePrimaryFab } from '../../components/ui/PagePrimaryFab';
import { useToast } from '../../components/ui/ToastProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import { resolveApiErrorMessage } from '../../i18n/mapApiClientError';
import { receiptApi, settingsApi } from '../../lib/api';
import { useOfflineSync } from '../../contexts/OfflineSyncContext';
import { blobToDataUrl } from '../../offline/dataUrl';
import { enqueueReceiptCreate } from '../../offline/offlineQueue';
import { AnalyzeHttpError, analyzeReceiptsSequential } from '../../lib/receiptAnalyzeApi';
import { emitReceiptScanUploadQuotaLimitIfInsufficient, emitReceiptSubmitQuotaLimitIfInsufficient, isLimitReachedError } from '../../lib/limitError';
import { setMainAppScrollTop } from '../../lib/mainAppScroll';
import { MODAL_BACKDROP_SCRIM_CLASS, MODAL_PANEL_SCROLL_DVH_CLASS } from '../../lib/modalClasses';
import {
  compressImageFile,
  createClientId,
  resolveClientImageMime,
  validateReceiptFile,
  type CompressedReceiptBlob,
} from '../../lib/receiptImagePreprocess';
import { resolvePairingScopedImageDisplayPath } from '../../lib/pairingImageStream';

type EditableRow = {
  key: string;
  fileName: string;
  analysisOk: boolean;
  storedImageUrl?: string;
  /** Signed GCS URL for UI preview only (submit still uses canonical storedImageUrl). */
  storedImageUrlForDisplay?: string;
  /** Local blob URL from the picked file; used for preview before save. */
  localPreviewUrl?: string;
  receipt: ReceiptData;
  receiptDate: string;
  submitStatus: 'idle' | 'loading' | 'ok' | 'error';
  submitError?: string;
  submittedReceiptId?: string;
  submittedReceiptStatus?: 'save' | 'submitted' | 'reviewed';
  helperRemark: string;
};

type SubmitRowResult = 'success_online' | 'success_offline' | 'failed';

type MismatchConfirmState =
  | { mode: 'single'; rowKey: string }
  | { mode: 'batch' }
  | null;

const formControlClass =
  'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

const itemShellClass =
  'grid grid-cols-12 gap-2 rounded-lg bg-gray-50 p-2 dark:bg-gray-800/90';

const itemFieldClass =
  'rounded border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const NAVIGATE_BACK_SENTINEL = '__NAVIGATE_BACK__';

const TrashIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-2">
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
);

/** Prefer budget-scoped receipt date; fall back to receipt line "date" when only that field was filled. */
const resolveSubmitReceiptDate = (row: EditableRow): string => {
  if (row.receiptDate && ISO_DATE_RE.test(row.receiptDate)) {
    return row.receiptDate;
  }
  const shop = row.receipt.date;
  if (shop && ISO_DATE_RE.test(shop)) {
    return shop;
  }
  return '';
};

const emptyReceipt = (): ReceiptData => ({
  company: '',
  shop: '',
  date: '',
  currency: '',
  totalAmount: '',
  items: [],
  remark: '',
});

const resolveEditableRowTotal = (row: EditableRow): number | null => {
  const total = parseReceiptTotalAmount(row.receipt.totalAmount);
  if (total === null || total < 0) {
    return null;
  }
  return total;
};

const getEditableRowSubmitError = (row: EditableRow, t: (key: string) => string): string | null => {
  if (!row.storedImageUrl || row.storedImageUrl.trim() === '') {
    return t('receipt.analyze.needImageUrl');
  }
  const effectiveReceiptDate = resolveSubmitReceiptDate(row);
  if (!effectiveReceiptDate) {
    return t('receipt.analyze.dateRequired');
  }
  if (row.receipt.company.trim() === '') {
    return t('receipt.analyze.companyRequired');
  }
  if (resolveEditableRowTotal(row) === null) {
    return t('receipt.analyze.invalidTotal');
  }
  return null;
};

const rowHasAmountMismatch = (row: EditableRow): boolean => {
  const total = resolveEditableRowTotal(row);
  if (total === null) {
    return false;
  }
  return isReceiptTotalMismatch(row.receipt.totalAmount, row.receipt.items);
};

const resolveSubmittedReceiptBadgeStatus = (row: EditableRow): ReceiptStatus => {
  const s = row.submittedReceiptStatus;
  if (s === 'submitted') {
    return RECEIPT_STATUS.SUBMITTED;
  }
  if (s === 'reviewed') {
    return RECEIPT_STATUS.REVIEWED;
  }
  return RECEIPT_STATUS.SAVE;
};

/** Preview during analyze/edit: prefer local blob, then pairing-scoped stream path for GCS URLs. */
const resolveRowPreviewSrc = (row: EditableRow): string => {
  const local = row.localPreviewUrl?.trim() ?? '';
  if (local !== '') {
    return local;
  }
  const displayOverride = row.storedImageUrlForDisplay?.trim() ?? '';
  if (displayOverride !== '') {
    return resolvePairingScopedImageDisplayPath(displayOverride);
  }
  const canonical = row.storedImageUrl?.trim() ?? '';
  if (canonical !== '') {
    return resolvePairingScopedImageDisplayPath(canonical);
  }
  return '';
};

const expandAnalyzeToRows = (
  response: { results: AnalyzeResult[] },
  sources: CompressedReceiptBlob[],
): EditableRow[] => {
  const previewByFileName = new Map<string, string>();
  for (const source of sources) {
    if (previewByFileName.has(source.fileName)) {
      continue;
    }
    previewByFileName.set(source.fileName, URL.createObjectURL(source.blob));
  }
  const out: EditableRow[] = [];
  for (const r of response.results) {
    const localPreviewUrl = previewByFileName.get(r.fileName);
    if (r.success && Array.isArray(r.data)) {
      r.data.forEach((rec) => {
        out.push({
          key: createClientId(),
          fileName: r.fileName,
          analysisOk: true,
          storedImageUrl: r.storedImageUrl,
          storedImageUrlForDisplay: r.storedImageUrlForDisplay,
          localPreviewUrl,
          receipt: { ...rec },
          receiptDate: rec.date && ISO_DATE_RE.test(rec.date) ? rec.date : '',
          submitStatus: 'idle',
          helperRemark: '',
        });
      });
    } else {
      out.push({
        key: createClientId(),
        fileName: r.fileName,
        analysisOk: false,
        storedImageUrl: r.storedImageUrl,
        storedImageUrlForDisplay: r.storedImageUrlForDisplay,
        localPreviewUrl,
        receipt: emptyReceipt(),
        receiptDate: '',
        submitStatus: 'idle',
        helperRemark: '',
      });
    }
  }
  return out;
};

export const ReceiptAnalyzePage = () => {
  const { t, language } = useLanguage();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const { refreshPendingCount } = useOfflineSync();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const pageErrorBannerRef = useRef<HTMLDivElement>(null);

  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [submittingAll, setSubmittingAll] = useState(false);
  const [submitAllProgress, setSubmitAllProgress] = useState({ current: 0, total: 0 });
  const pageBusy = analyzing || submittingAll;
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [removeTargetKey, setRemoveTargetKey] = useState<string | null>(null);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [mismatchConfirm, setMismatchConfirm] = useState<MismatchConfirmState>(null);
  const [pendingNavigateHref, setPendingNavigateHref] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [rateInfo, setRateInfo] = useState<string | null>(null);
  const [nextStepSheetOpen, setNextStepSheetOpen] = useState(false);
  const [nextStepReceiptDates, setNextStepReceiptDates] = useState<string[]>([]);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const hasUnsubmittedRows = rows.some((row) => row.submitStatus !== 'ok');
  const allRowsSaved = rows.length > 0 && rows.every((row) => row.submitStatus === 'ok');
  const shouldBlockLeave = (hasUnsubmittedRows && !analyzing) || submittingAll;

  useEffect(() => {
    if (!allRowsSaved) {
      return;
    }
    setMainAppScrollTop(0);
  }, [allRowsSaved]);

  useEffect(() => {
    if (!pageError || !pageErrorBannerRef.current) {
      return;
    }
    pageErrorBannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [pageError]);

  useEffect(() => {
    if (!shouldBlockLeave) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlockLeave]);

  useEffect(() => {
    return () => {
      const seen = new Set<string>();
      for (const row of rowsRef.current) {
        const localUrl = row.localPreviewUrl?.trim();
        if (localUrl && localUrl.startsWith('blob:') && !seen.has(localUrl)) {
          seen.add(localUrl);
          URL.revokeObjectURL(localUrl);
        }
      }
    };
  }, []);

  const handlePageClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!shouldBlockLeave || event.defaultPrevented) {
        return;
      }
      if (!(event.target instanceof Element)) {
        return;
      }
      const anchor = event.target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) {
        return;
      }
      if (anchor.target && anchor.target !== '_self') {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const nextUrl = new URL(anchor.href, window.location.href);
      const currentUrl = new URL(window.location.href);
      const isSameLocation =
        nextUrl.origin === currentUrl.origin &&
        nextUrl.pathname === currentUrl.pathname &&
        nextUrl.search === currentUrl.search &&
        nextUrl.hash === currentUrl.hash;
      if (isSameLocation) {
        return;
      }
      event.preventDefault();
      const nextHref = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      setPendingNavigateHref(nextHref);
      setLeaveConfirmOpen(true);
    },
    [shouldBlockLeave]
  );

  const handleCameraPickClick = () => {
    if (pageBusy) return;
    cameraInputRef.current?.click();
  };

  const handleGalleryPickClick = () => {
    if (pageBusy) return;
    galleryInputRef.current?.click();
  };

  const handleReceiptPatch = useCallback((key: string, patch: Partial<ReceiptData>) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const nextReceipt = { ...row.receipt, ...patch };
        const d = patch.date;
        const syncReceiptDate =
          row.receiptDate === '' && typeof d === 'string' && ISO_DATE_RE.test(d);
        return {
          ...row,
          receipt: nextReceipt,
          receiptDate: syncReceiptDate ? d : row.receiptDate,
          submitError: undefined,
        };
      })
    );
  }, []);

  const handleItemPatch = useCallback(
    (key: string, itemIndex: number, patch: Partial<ReceiptItem>) => {
      setRows((prev) =>
        prev.map((row) => {
          if (row.key !== key) return row;
          const nextItems = row.receipt.items.map((it, i) =>
            i === itemIndex ? { ...it, ...patch } : it
          );
          return { ...row, receipt: { ...row.receipt, items: nextItems }, submitError: undefined };
        })
      );
    },
    []
  );

  const handleAddItem = useCallback((key: string) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const nextItems: ReceiptItem[] = [
          ...row.receipt.items,
          { item: '', qty: '', amount: '' },
        ];
        return {
          ...row,
          receipt: { ...row.receipt, items: nextItems },
          submitError: undefined,
        };
      })
    );
  }, []);

  const handleAddItemKeyDown =
    (key: string) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleAddItem(key);
      }
    };

  const handleRemoveItem = useCallback((key: string, itemIndex: number) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const nextItems = row.receipt.items.filter((_, i) => i !== itemIndex);
        return {
          ...row,
          receipt: { ...row.receipt, items: nextItems },
          submitError: undefined,
        };
      })
    );
  }, []);

  const handleRowMeta = useCallback((key: string, patch: Partial<Pick<EditableRow, 'receiptDate'>>) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch, submitError: undefined } : row))
    );
  }, []);

  const handleRemoveRow = useCallback((key: string) => {
    setRows((prev) => {
      const row = prev.find((r) => r.key === key);
      if (!row) {
        return prev;
      }
      const next = prev.filter((r) => r.key !== key);
      const localUrl = row.localPreviewUrl?.trim();
      if (localUrl && localUrl.startsWith('blob:')) {
        const stillUsed = next.some((r) => r.localPreviewUrl?.trim() === localUrl);
        if (!stillUsed) {
          URL.revokeObjectURL(localUrl);
        }
      }
      const legacyUrl = row.storedImageUrl;
      if (legacyUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(legacyUrl);
      }
      return next;
    });
    setPageError(null);
  }, []);

  const handleOpenRemoveConfirm = useCallback((key: string) => {
    setRemoveTargetKey(key);
  }, []);

  const handleCancelRemoveConfirm = useCallback(() => {
    setRemoveTargetKey(null);
  }, []);

  const handleConfirmRemoveRow = useCallback(() => {
    if (!removeTargetKey) return;
    handleRemoveRow(removeTargetKey);
    setRemoveTargetKey(null);
  }, [handleRemoveRow, removeTargetKey]);

  const handleCancelLeave = useCallback(() => {
    setPendingNavigateHref(null);
    setLeaveConfirmOpen(false);
  }, []);

  const handleConfirmLeave = useCallback(() => {
    if (pendingNavigateHref) {
      if (pendingNavigateHref === NAVIGATE_BACK_SENTINEL) {
        navigate(-1);
        setPendingNavigateHref(null);
        setLeaveConfirmOpen(false);
        return;
      }
      navigate(pendingNavigateHref);
    }
    setPendingNavigateHref(null);
    setLeaveConfirmOpen(false);
  }, [navigate, pendingNavigateHref]);

  const handleBackButtonClick = useCallback(() => {
    if (!shouldBlockLeave) {
      navigate(-1);
      return;
    }
    setPendingNavigateHref(NAVIGATE_BACK_SENTINEL);
    setLeaveConfirmOpen(true);
  }, [navigate, shouldBlockLeave]);

  const runAnalysis = async (compressed: CompressedReceiptBlob[]) => {
    setAnalyzing(true);
    setPageError(null);
    setRateInfo(null);
    try {
      const res = await analyzeReceiptsSequential(
        compressed.map((c) => ({ blob: c.blob, fileName: c.fileName })),
        (current, total) => {
          setProgress({ current, total });
        }
      );
      for (const r of res.results) {
        if (!r.success) {
          console.warn('[Receipt analyze] File failed', r.fileName, r.error);
        }
      }
      const nextRows = expandAnalyzeToRows(res, compressed);
      setRows((prev) => [...prev, ...nextRows]);
    } catch (e) {
      if (isLimitReachedError(e)) {
        return;
      }
      if (e instanceof AnalyzeHttpError) {
        const b = e.body;
        if (e.status === 429) {
          return;
        }
        setPageError(typeof b.error === 'string' ? b.error : t('receipt.analyze.analyzeError'));
      } else {
        setPageError(t('receipt.analyze.analyzeError'));
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handlePickFiles = async (fileList: File[]) => {
    if (fileList.length === 0) return;

    if (fileList.length > 10) {
      setPageError(t('receipt.analyze.tooManyFiles'));
      return;
    }

    setPageError(null);
    setRateInfo(null);

    const usageRes = await settingsApi.getAiUsage();
    if (
      emitReceiptScanUploadQuotaLimitIfInsufficient(
        {
          ai: usageRes.data?.ai,
          receipt: usageRes.data?.receipt,
        },
        fileList.length,
      )
    ) {
      return;
    }

    setAnalyzing(true);
    setProgress({ current: 0, total: fileList.length });

    const compressed: CompressedReceiptBlob[] = [];
    for (const f of fileList) {
      const v = validateReceiptFile(f);
      if (v === 'tooLarge') {
        setAnalyzing(false);
        setPageError(t('receipt.analyze.fileTooLarge'));
        return;
      }
      if (v === 'badType') {
        setAnalyzing(false);
        setPageError(t('receipt.analyze.badType'));
        return;
      }
      try {
        compressed.push(await compressImageFile(f));
      } catch {
        const mime = resolveClientImageMime(f);
        if (!mime) {
          setAnalyzing(false);
          setPageError(t('receipt.analyze.badType'));
          return;
        }
        compressed.push({ blob: f, fileName: f.name, mime });
      }
    }

    await runAnalysis(compressed);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = Array.from(event.target.files ?? []);
    event.target.value = '';
    await handlePickFiles(fileList);
  };

  const handleSubmitRow = async (
    key: string,
    options?: { silentSuccessToast?: boolean; skipReceiptQuotaPreCheck?: boolean }
  ): Promise<SubmitRowResult> => {
    const silentSuccessToast = options?.silentSuccessToast === true;
    const skipReceiptQuotaPreCheck = options?.skipReceiptQuotaPreCheck === true;
    const row = rows.find((r) => r.key === key);
    if (!row) return 'failed';
    const resetRowIdle = () => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === key ? { ...r, submitStatus: 'idle' as const, submitError: undefined } : r
        )
      );
    };
    const failWithMessage = (message: string) => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === key ? { ...r, submitStatus: 'error' as const, submitError: message } : r
        )
      );
    };
    const submitError = getEditableRowSubmitError(row, t);
    if (submitError) {
      failWithMessage(submitError);
      return 'failed';
    }
    const total = resolveEditableRowTotal(row);
    if (total === null) {
      failWithMessage(t('receipt.analyze.invalidTotal'));
      return 'failed';
    }
    const effectiveReceiptDate = resolveSubmitReceiptDate(row);
    const storedImageUrl = row.storedImageUrl?.trim() ?? '';

    const cleanedItems = row.receipt.items
      .filter((it) => it.item.trim() !== '')
      .map((it) => ({
        item: it.item.trim(),
        qty: it.qty,
        amount: it.amount,
      }));

    if (navigator.onLine && !skipReceiptQuotaPreCheck) {
      const usageRes = await settingsApi.getAiUsage();
      if (emitReceiptSubmitQuotaLimitIfInsufficient(usageRes.data?.receipt, 1)) {
        return 'failed';
      }
    }

    setRows((prev) =>
      prev.map((r) =>
        r.key === key
          ? { ...r, submitStatus: 'loading' as const, submitError: undefined }
          : r
      )
    );
    setPageError(null);

    try {
      if (!navigator.onLine) {
        const imgUrl = row.localPreviewUrl?.trim() || row.storedImageUrl;
        if (!imgUrl || imgUrl.trim() === '') {
          failWithMessage(t('receipt.analyze.needImageUrl'));
          return 'failed';
        }
        const originalImageUrlOrData = imgUrl.startsWith('blob:')
          ? await blobToDataUrl(await fetch(imgUrl).then((r) => r.blob()))
          : imgUrl;
        try {
          await enqueueReceiptCreate({
            payload: {
              receiptDate: effectiveReceiptDate,
              company: row.receipt.company.trim(),
              shop: row.receipt.shop.trim(),
              currency: DEFAULT_CURRENCY,
              totalAmount: total,
              items: cleanedItems,
              remark: row.helperRemark.trim(),
              aiRawResponse: JSON.stringify({ receipts: [row.receipt] }),
            },
            originalImageUrlOrData,
          });
          await refreshPendingCount();
          setRows((prev) =>
            prev.map((r) =>
              r.key === key
                ? {
                    ...r,
                    submitStatus: 'ok' as const,
                    submitError: undefined,
                    submittedReceiptId: undefined,
                    submittedReceiptStatus: 'save',
                  }
                : r
            )
          );
          if (!silentSuccessToast) {
            showSuccessToast(t('toast.receiptAnalyze.singleSavedOffline'));
          }
          return 'success_offline';
        } catch (err) {
          failWithMessage(resolveApiErrorMessage(err, t, 'receipt.analyze.submitError'));
          return 'failed';
        }
      }

      const res = await receiptApi.create({
        receiptDate: effectiveReceiptDate,
        company: row.receipt.company.trim(),
        shop: row.receipt.shop.trim(),
        currency: DEFAULT_CURRENCY,
        totalAmount: total,
        items: cleanedItems,
        remark: row.helperRemark.trim(),
        aiRawResponse: JSON.stringify({ receipts: [row.receipt] }),
        originalImageUrl: storedImageUrl,
      });

      if (!res.success || !res.data) {
        const errMsg =
          typeof res.error === 'string' && res.error.trim() !== ''
            ? res.error
            : t('receipt.analyze.submitError');
        failWithMessage(errMsg);
        return 'failed';
      }

      const created = res.data;

      setRows((prev) =>
        prev.map((r) =>
          r.key === key
            ? {
                ...r,
                submitStatus: 'ok' as const,
                submitError: undefined,
                submittedReceiptId: created.id,
                submittedReceiptStatus: created.status,
              }
            : r
        )
      );
      if (!silentSuccessToast) {
        const unsavedRemaining = rowsRef.current.filter(
          (r) => r.key !== key && r.submitStatus !== 'ok',
        ).length;
        if (unsavedRemaining > 0) {
          showSuccessToast(
            t('toast.receiptAnalyze.singleSavedStillUnsaved', { count: unsavedRemaining }),
          );
        } else {
          showSuccessToast(t('toast.receiptAnalyze.singleSavedOnline'));
          const savedDates = rowsRef.current
            .filter((r) => r.key === key || r.submitStatus === 'ok')
            .map((r) => resolveSubmitReceiptDate(r.key === key ? row : r))
            .filter((date) => ISO_DATE_RE.test(date));
          setNextStepReceiptDates(
            savedDates.length > 0 ? savedDates : [resolveSubmitReceiptDate(row)],
          );
          setNextStepSheetOpen(true);
        }
      }
      return 'success_online';
    } catch (err) {
      if (isLimitReachedError(err)) {
        resetRowIdle();
        return 'failed';
      }
      failWithMessage(resolveApiErrorMessage(err, t, 'receipt.analyze.submitError'));
      return 'failed';
    }
  };

  const handleSubmitAllRows = async () => {
    const submitTargetRows = rows.filter((row) => row.submitStatus !== 'ok');
    if (submitTargetRows.length === 0) {
      return;
    }

    if (navigator.onLine) {
      const usageRes = await settingsApi.getAiUsage();
      if (emitReceiptSubmitQuotaLimitIfInsufficient(usageRes.data?.receipt, submitTargetRows.length)) {
        return;
      }
    }

    setSubmittingAll(true);
    setSubmitAllProgress({ current: 0, total: submitTargetRows.length });
    try {
      let successOnlineCount = 0;
      let successOfflineCount = 0;
      let failedCount = 0;
      const onlineSuccessDates: string[] = [];
      for (let i = 0; i < submitTargetRows.length; i++) {
        const row = submitTargetRows[i];
        const result = await handleSubmitRow(row.key, {
          silentSuccessToast: true,
          skipReceiptQuotaPreCheck: true,
        });
        setSubmitAllProgress({ current: i + 1, total: submitTargetRows.length });
        if (result === 'success_online') {
          successOnlineCount += 1;
          const receiptDate = resolveSubmitReceiptDate(row);
          if (ISO_DATE_RE.test(receiptDate)) {
            onlineSuccessDates.push(receiptDate);
          }
          continue;
        }
        if (result === 'success_offline') {
          successOfflineCount += 1;
          continue;
        }
        failedCount += 1;
      }

      const openSettlementNextStepSheet = (): void => {
        if (onlineSuccessDates.length === 0) {
          return;
        }
        setNextStepReceiptDates(onlineSuccessDates);
        setNextStepSheetOpen(true);
      };

      if (failedCount === 0 && successOfflineCount === 0 && successOnlineCount > 0) {
        showSuccessToast(
          t('toast.receiptAnalyze.batchAllOnlineSuccess', { successCount: successOnlineCount })
        );
        openSettlementNextStepSheet();
        return;
      }

      if (failedCount === 0 && successOfflineCount > 0 && successOnlineCount === 0) {
        showSuccessToast(
          t('toast.receiptAnalyze.batchAllOfflineQueued', { queuedCount: successOfflineCount })
        );
        return;
      }

      if (failedCount === 0 && successOfflineCount > 0 && successOnlineCount > 0) {
        showSuccessToast(
          t('toast.receiptAnalyze.batchMixedOnlineOfflineSuccess', {
            onlineCount: successOnlineCount,
            queuedCount: successOfflineCount,
          })
        );
        return;
      }

      if (successOnlineCount > 0 || successOfflineCount > 0) {
        if (failedCount > 0) {
          showSuccessToast(
            t('toast.receiptAnalyze.singleSavedStillUnsaved', { count: failedCount }),
          );
        } else {
          showSuccessToast(
            t('toast.receiptAnalyze.batchPartialResult', {
              onlineCount: successOnlineCount,
              queuedCount: successOfflineCount,
              failedCount,
            })
          );
        }
      }
    } finally {
      setSubmittingAll(false);
    }
  };

  const failRowWithMessage = (key: string, message: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.key === key ? { ...r, submitStatus: 'error' as const, submitError: message } : r
      )
    );
  };

  const handleRequestSubmitRow = (key: string) => {
    const row = rows.find((r) => r.key === key);
    if (!row) {
      return;
    }
    const submitError = getEditableRowSubmitError(row, t);
    if (submitError) {
      failRowWithMessage(key, submitError);
      return;
    }
    if (rowHasAmountMismatch(row)) {
      setMismatchConfirm({ mode: 'single', rowKey: key });
      return;
    }
    void handleSubmitRow(key);
  };

  const handleRequestSubmitAllRows = () => {
    const submitTargetRows = rows.filter((row) => row.submitStatus !== 'ok');
    const hasMismatch = submitTargetRows.some(
      (row) => getEditableRowSubmitError(row, t) === null && rowHasAmountMismatch(row)
    );
    if (hasMismatch) {
      setMismatchConfirm({ mode: 'batch' });
      return;
    }
    void handleSubmitAllRows();
  };

  const handleCancelMismatchConfirm = () => {
    setMismatchConfirm(null);
  };

  const handleConfirmMismatchSave = () => {
    if (!mismatchConfirm) {
      return;
    }
    if (mismatchConfirm.mode === 'single') {
      const rowKey = mismatchConfirm.rowKey;
      setMismatchConfirm(null);
      void handleSubmitRow(rowKey);
      return;
    }
    setMismatchConfirm(null);
    void handleSubmitAllRows();
  };

  const mismatchModalRows = mismatchConfirm
    ? rows.filter((row) => {
        if (row.submitStatus === 'ok') {
          return false;
        }
        if (getEditableRowSubmitError(row, t) !== null) {
          return false;
        }
        return rowHasAmountMismatch(row);
      })
    : [];

  const handleScanMoreReceipts = () => {
    setRows([]);
    setPageError(null);
    setRateInfo(null);
    setRemoveTargetKey(null);
    setMismatchConfirm(null);
    setLeaveConfirmOpen(false);
    setPendingNavigateHref(null);
  };

  return (
    <div
      className="min-h-full bg-gray-50 dark:bg-gray-950"
      onClickCapture={handlePageClickCapture}
    >
      <header className="bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/20">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={handleBackButtonClick}
              disabled={pageBusy}
              aria-label={t('task.action.back')}
              className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4L6 10l6 6" />
              </svg>
            </button>
            <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('receipt.analyze.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('receipt.analyze.subtitle')}</p>
            </div>
          </div>
        </div>
      </header>

      <main
        className={`mx-auto max-w-lg px-4 py-6 pb-28${pageBusy ? ' pointer-events-none' : ''}`}
        aria-busy={pageBusy}
      >
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
          capture="environment"
          className="sr-only"
          aria-hidden
          tabIndex={-1}
          onChange={handleFileChange}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
          multiple
          className="sr-only"
          aria-hidden
          tabIndex={-1}
          onChange={handleFileChange}
        />

        {rows.length === 0 ? (
          <div className="mb-4 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('receipt.analyze.pickImages')}
              </h2>
            </div>
            <div
              className={`rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/40 ${
                analyzing ? 'pointer-events-none opacity-60' : ''
              }`}
            >
              <div className="flex h-full flex-col items-center justify-center gap-3">
                <div className="flex w-full items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={handleCameraPickClick}
                    disabled={analyzing}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600 transition-colors hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-primary-900/40 dark:text-primary-300 dark:hover:bg-primary-900/60"
                    aria-label={t('task.complete.takePhoto')}
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleGalleryPickClick}
                    disabled={analyzing}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    aria-label={t('task.complete.choosePhoto')}
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                </div>
                <p className="max-w-[220px] text-center text-xs text-gray-500 dark:text-gray-400">
                  {t('task.complete.photoHint')}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {pageError ? (
          <div
            ref={pageErrorBannerRef}
            className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-200"
            role="alert"
            aria-live="assertive"
            tabIndex={-1}
          >
            {pageError}
          </div>
        ) : null}

        {rateInfo ? (
          <div
            className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
            role="status"
          >
            {rateInfo}
          </div>
        ) : null}

        {rows.length > 0 ? (
          <div className="space-y-6">
            {rows.map((row, rowIndex) => {
              const previewUrl = resolveRowPreviewSrc(row);
              const photoHeadingLabel =
                rows.length > 1
                  ? t('receipt.analyze.photoHeadingOfTotal', {
                      current: rowIndex + 1,
                      total: rows.length,
                    })
                  : t('receipt.analyze.photoHeading');
              const aiRemark = row.receipt.remark.trim();
              const submittedDate = resolveSubmitReceiptDate(row);
              const rowLineSum = sumReceiptLineItemAmounts(row.receipt.items);
              const rowResolvedTotal = resolveEditableRowTotal(row);
              const showAmountMismatchBanner =
                rowResolvedTotal !== null && rowHasAmountMismatch(row);
              const totalLabel =
                row.receipt.totalAmount === ''
                  ? '—'
                  : typeof row.receipt.totalAmount === 'number'
                    ? `$${row.receipt.totalAmount.toFixed(2)}`
                    : `$${String(row.receipt.totalAmount)}`;

              if (row.submitStatus === 'ok') {
                return (
                  <section
                    key={row.key}
                    className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/20"
                    aria-label={row.fileName}
                  >
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <h3 className="min-w-0 flex-1 text-sm font-semibold text-gray-800 dark:text-gray-200">
                        {photoHeadingLabel}
                      </h3>
                      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                        <StatusBadge status={resolveSubmittedReceiptBadgeStatus(row)} size="sm" />
                        {row.submittedReceiptStatus === 'submitted' ? (
                          <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                            {t('receipt.analyze.submittedDueToPendingSettlement')}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {row.submittedReceiptId || previewUrl ? (
                      <div className="mb-3">
                        {row.submittedReceiptId ? (
                          <ReceiptImageLightbox
                            receiptId={row.submittedReceiptId}
                            alt={t('receipt.analyze.photoAlt')}
                            enlargeAriaLabel={t('receipt.analyze.enlargeImageAria')}
                            closeLabel={t('common.closeViewer')}
                          />
                        ) : (
                          <ReceiptImageLightbox
                            previewSrc={previewUrl}
                            alt={t('receipt.analyze.photoAlt')}
                            enlargeAriaLabel={t('receipt.analyze.enlargeImageAria')}
                            closeLabel={t('common.closeViewer')}
                          />
                        )}
                      </div>
                    ) : null}
                    <div className="mt-3 flex items-end justify-between gap-3">
                      <dl className="flex-1 space-y-1 text-sm text-gray-800 dark:text-gray-200">
                        <div className="flex justify-between gap-2">
                          <dt className="text-gray-500 dark:text-gray-400">
                            {t('receipt.analyze.dateLabel')}
                          </dt>
                          <dd className="font-medium">{submittedDate || '—'}</dd>
                        </div>
                        <div className="flex justify-between gap-2">
                          <dt className="text-gray-500 dark:text-gray-400">
                            {t('receipt.analyze.field.total')}
                          </dt>
                          <dd className="font-medium">{totalLabel}</dd>
                        </div>
                      </dl>
                      {row.submittedReceiptId ? (
                        <Link
                          to={`/receipts/${row.submittedReceiptId}`}
                          aria-label={t('receipt.analyze.openDetailAria')}
                          className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                          tabIndex={0}
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      ) : null}
                    </div>
                    {!row.submittedReceiptId ? (
                      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                        {t('receipt.analyze.savedOffline')}
                      </p>
                    ) : null}
                  </section>
                );
              }

              return (
                <section
                  key={row.key}
                  className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/20"
                  aria-label={row.fileName}
                >
                  {row.submitStatus === 'error' && row.submitError ? (
                    <p className="mb-3 text-sm text-red-600 dark:text-red-400" role="alert">
                      {row.submitError}
                    </p>
                  ) : null}
                  <div className="mb-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <h3 className="min-w-0 flex-1 pr-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
                        {photoHeadingLabel}
                      </h3>
                      <button
                        type="button"
                        onClick={() => handleOpenRemoveConfirm(row.key)}
                        aria-label={t('receipt.analyze.removeRow')}
                        title={t('receipt.analyze.removeRow')}
                        className="inline-flex shrink-0 items-center justify-center rounded p-1 text-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 dark:text-red-400"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                    {previewUrl ? (
                      <ReceiptImageLightbox
                        previewSrc={previewUrl}
                        alt={t('receipt.analyze.photoAlt')}
                        enlargeAriaLabel={t('receipt.analyze.enlargeImageAria')}
                        closeLabel={t('common.closeViewer')}
                      />
                    ) : null}
                  </div>
                  {aiRemark !== '' ? (
                    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800/70 dark:bg-amber-950/30">
                      <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                        {t('receipt.analyze.aiRemarkLabel')}
                      </p>
                      <p className="mt-1 text-sm text-amber-900 dark:text-amber-100">{aiRemark}</p>
                    </div>
                  ) : null}
                  {!row.analysisOk ? (
                    <p className="mb-3 text-sm text-amber-800 dark:text-amber-200">
                      {t('receipt.analyze.manualHint')}
                    </p>
                  ) : null}

                  <div className="grid gap-3">
                    <IsoDatePicker
                      id={`receipt-meta-date-${row.key}`}
                      label={t('receipt.analyze.dateLabel')}
                      value={row.receiptDate}
                      onChange={(next) => handleRowMeta(row.key, { receiptDate: next })}
                      maxDate={getTodayDate()}
                      captionLayout="dropdown"
                    />

                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      {t('receipt.analyze.field.company')}
                      <input
                        type="text"
                        value={row.receipt.company}
                        onChange={(e) => handleReceiptPatch(row.key, { company: e.target.value })}
                        className={formControlClass}
                      />
                    </label>

                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      {t('receipt.analyze.field.total')}
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={
                          row.receipt.totalAmount === ''
                            ? ''
                            : String(row.receipt.totalAmount)
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '') {
                            handleReceiptPatch(row.key, { totalAmount: '' });
                            return;
                          }
                          const n = parseFloat(v);
                          handleReceiptPatch(row.key, {
                            totalAmount: Number.isFinite(n) ? n : '',
                          });
                        }}
                        className={formControlClass}
                      />
                      {showAmountMismatchBanner && rowResolvedTotal !== null ? (
                        <ReceiptTotalMismatchBanner
                          message={t('receipt.analyze.totalMismatchBanner', {
                            total: formatReceiptMoney(rowResolvedTotal),
                            lineSum: formatReceiptMoney(rowLineSum),
                          })}
                        />
                      ) : null}
                    </label>

                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      {t('receipt.analyze.field.remark')}
                      <textarea
                        value={row.helperRemark}
                        onChange={(e) =>
                          setRows((prev) =>
                            prev.map((r) =>
                              r.key === row.key
                                ? { ...r, helperRemark: e.target.value, submitError: undefined }
                                : r
                            )
                          )
                        }
                        rows={2}
                        className={formControlClass}
                      />
                    </label>

                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {t('receipt.analyze.items')}
                      </p>
                      <div className="mt-2 grid grid-cols-12 gap-2 rounded-lg bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                        <p className="col-span-12 sm:col-span-5">{t('receipt.analyze.itemName')}</p>
                        <p className="col-span-4 sm:col-span-3">{t('receipt.analyze.qty')}</p>
                        <p className="col-span-6 sm:col-span-3">{t('receipt.analyze.amount')}</p>
                        <p className="col-span-2 text-right sm:col-span-1">
                          {t('receipt.analyze.itemsAction')}
                        </p>
                      </div>
                      <ul className="mt-2 space-y-2">
                        {row.receipt.items.map((it, idx) => (
                          <li
                            key={`${row.key}-item-${idx}`}
                            className={itemShellClass}
                          >
                            <input
                              type="text"
                              aria-label={t('receipt.analyze.itemName')}
                              value={it.item}
                              onChange={(e) =>
                                handleItemPatch(row.key, idx, { item: e.target.value })
                              }
                              className={`col-span-12 sm:col-span-5 ${itemFieldClass}`}
                            />
                            <input
                              type="text"
                              aria-label={t('receipt.analyze.qty')}
                              value={it.qty === '' ? '' : String(it.qty)}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') {
                                  handleItemPatch(row.key, idx, { qty: '' });
                                  return;
                                }
                                const n = Number(raw);
                                handleItemPatch(row.key, idx, {
                                  qty: Number.isFinite(n) && raw === String(n) ? n : raw,
                                });
                              }}
                              className={`col-span-4 sm:col-span-3 ${itemFieldClass}`}
                            />
                            <input
                              type="number"
                              aria-label={t('receipt.analyze.amount')}
                              value={it.amount === '' ? '' : it.amount}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '') {
                                  handleItemPatch(row.key, idx, { amount: '' });
                                  return;
                                }
                                const n = parseFloat(v);
                                handleItemPatch(row.key, idx, {
                                  amount: Number.isFinite(n) ? n : '',
                                });
                              }}
                              className={`col-span-6 sm:col-span-3 ${itemFieldClass}`}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(row.key, idx)}
                              aria-label={t('receipt.analyze.removeItem')}
                              title={t('receipt.analyze.removeItem')}
                              className="col-span-2 inline-flex items-center justify-center rounded border border-red-200 text-red-700 sm:col-span-1 dark:border-red-800 dark:text-red-300"
                            >
                              <TrashIcon />
                            </button>
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        onClick={() => handleAddItem(row.key)}
                        onKeyDown={handleAddItemKeyDown(row.key)}
                        aria-label={t('receipt.analyze.addItem')}
                        className="mt-2 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-primary-300 bg-primary-50/90 px-4 py-2.5 text-sm font-semibold text-primary-800 shadow-sm transition-colors hover:border-primary-400 hover:bg-primary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-primary-600/60 dark:bg-primary-950/50 dark:text-primary-200 dark:hover:bg-primary-900/60 sm:w-auto"
                      >
                        <span className="text-base font-bold leading-none" aria-hidden>
                          +
                        </span>
                        <span>{t('receipt.analyze.addItem')}</span>
                      </button>
                    </div>

                    <AppButton
                      type="button"
                      variant="primary"
                      className="mt-2 shadow-sm"
                      onClick={() => handleRequestSubmitRow(row.key)}
                      disabled={row.submitStatus === 'loading' || submittingAll}
                      aria-label={
                        row.submitStatus === 'loading'
                          ? t('receipt.analyze.submitting')
                          : t('receipt.analyze.submit')
                      }
                      aria-busy={row.submitStatus === 'loading'}
                    >
                      {row.submitStatus === 'loading'
                        ? t('receipt.analyze.submitting')
                        : t('receipt.analyze.submit')}
                    </AppButton>
                  </div>
                </section>
              );
            })}
          </div>
        ) : null}

        {rows.filter((row) => row.submitStatus !== 'ok').length > 1 ? (
          <AppButton
            type="button"
            variant="primary"
            fullWidth
            className="mt-4 shadow-sm"
            onClick={() => handleRequestSubmitAllRows()}
            disabled={rows.some((row) => row.submitStatus === 'loading') || submittingAll}
          >
            {t('receipt.analyze.submitAll')}
          </AppButton>
        ) : null}

      </main>

      {allRowsSaved ? (
        <PagePrimaryFab onClick={handleScanMoreReceipts} ariaLabel={t('receipt.analyze.scanMore')}>
          <span className="text-lg font-bold leading-none" aria-hidden>
            +
          </span>
          <span>{t('receipt.analyze.scanMore')}</span>
        </PagePrimaryFab>
      ) : null}

      {analyzing ? (
        <div
          className={`fixed inset-0 z-50 flex flex-col items-center justify-center overflow-y-auto p-6 ${MODAL_BACKDROP_SCRIM_CLASS}`}
          role="dialog"
          aria-modal="true"
          aria-busy="true"
          aria-live="polite"
          aria-label={t('receipt.analyze.analyzing')}
        >
          <div className={`w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl dark:bg-gray-900 dark:ring-1 dark:ring-gray-700 ${MODAL_PANEL_SCROLL_DVH_CLASS}`}>
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
            <p className="font-semibold text-gray-900 dark:text-gray-100">{t('receipt.analyze.analyzing')}</p>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {t('receipt.analyze.analyzingProgress', {
                current: progress.current,
                total: Math.max(progress.total, 1),
              })}
            </p>
          </div>
        </div>
      ) : null}

      {submittingAll ? (
        <div
          className={`fixed inset-0 z-50 flex flex-col items-center justify-center overflow-y-auto p-6 ${MODAL_BACKDROP_SCRIM_CLASS}`}
          role="dialog"
          aria-modal="true"
          aria-busy="true"
          aria-live="polite"
          aria-label={t('receipt.analyze.submitting')}
        >
          <div className={`w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl dark:bg-gray-900 dark:ring-1 dark:ring-gray-700 ${MODAL_PANEL_SCROLL_DVH_CLASS}`}>
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
            <p className="font-semibold text-gray-900 dark:text-gray-100">{t('receipt.analyze.submitting')}</p>
            {submitAllProgress.total > 1 ? (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {t('receipt.analyze.submittingAllProgress', {
                  current: submitAllProgress.current,
                  total: Math.max(submitAllProgress.total, 1),
                })}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={mismatchConfirm !== null}
        title={t('receipt.analyze.totalMismatchConfirmTitle')}
        description={
          mismatchConfirm?.mode === 'single'
            ? (() => {
                const row = rows.find((r) => r.key === mismatchConfirm.rowKey);
                const total = row ? resolveEditableRowTotal(row) : null;
                const lineSum = row ? sumReceiptLineItemAmounts(row.receipt.items) : 0;
                if (total === null) {
                  return '';
                }
                return t('receipt.analyze.totalMismatchConfirmSingle', {
                  total: formatReceiptMoney(total),
                  lineSum: formatReceiptMoney(lineSum),
                });
              })()
            : t('receipt.analyze.totalMismatchConfirmBatchIntro')
        }
        confirmLabel={t('receipt.analyze.totalMismatchConfirmConfirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirmMismatchSave}
        onCancel={handleCancelMismatchConfirm}
      >
        {mismatchConfirm?.mode === 'batch' ? (
          <div className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-300">
            <ul className="list-none space-y-2">
              {mismatchModalRows.map((row) => {
                const total = resolveEditableRowTotal(row);
                if (total === null) {
                  return null;
                }
                const lineSum = sumReceiptLineItemAmounts(row.receipt.items);
                const receiptDate = resolveSubmitReceiptDate(row);
                const company = row.receipt.company.trim() || '—';
                return (
                  <li key={row.key}>
                    {t('receipt.analyze.totalMismatchConfirmBatchLine', {
                      date: formatReceiptMismatchDateLabel(receiptDate, language),
                      company,
                      total: formatReceiptMoney(total),
                      lineSum: formatReceiptMoney(lineSum),
                    })}
                  </li>
                );
              })}
            </ul>
            <p>{t('receipt.analyze.totalMismatchConfirmBatchFooter')}</p>
          </div>
        ) : null}
      </ConfirmDialog>
      <ConfirmDialog
        open={removeTargetKey !== null}
        title={t('receipt.analyze.removeConfirmTitle')}
        description={t('receipt.analyze.removeConfirmBody')}
        confirmLabel={t('receipt.analyze.removeRow')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirmRemoveRow}
        onCancel={handleCancelRemoveConfirm}
        variant="danger"
      />
      <ConfirmDialog
        open={leaveConfirmOpen}
        title={t('receipt.analyze.leaveConfirmTitle')}
        description={t('receipt.analyze.leaveConfirmBody')}
        confirmLabel={t('receipt.analyze.leaveConfirmLeave')}
        cancelLabel={t('receipt.analyze.leaveConfirmStay')}
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
      <HelperSettlementNextStepSheet
        open={nextStepSheetOpen}
        receiptDates={nextStepReceiptDates}
        onClose={() => setNextStepSheetOpen(false)}
      />
    </div>
  );
};
